import { useEffect, useMemo } from 'react'

import { type AxiosError } from 'axios'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { type UseQueryResult, useQuery } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Loader } from 'components/Loader/Loader'
import { addToast } from 'components/Toast/Toast'
import { LiveStreamViewer } from 'features/LiveStream/LiveStreamViewer/LiveStreamViewer'
import { getPerformerByUsername } from 'services/endpoints/profile'
import { type IProfile } from 'types/interfaces/IProfile'

type WatchLocationState = {
  data?: Partial<IProfile> & {
    id?: string
    username?: string
    avatar?: any
    active_stream?: boolean
    stream_url?: string | null
  }
}

export const Watch = () => {
  const portal = document.getElementById('portal')
  if (!portal) return null

  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { username } = useParams<string>()

  const stateData = (location.state as WatchLocationState | null)?.data

  const hasEnoughPrefetchedData = useMemo(() => {
    return !!(
      stateData &&
      stateData.username &&
      stateData.id &&
      stateData.avatar
    )
  }, [stateData])

  const {
    data,
    isLoading,
    error,
  }: UseQueryResult<IProfile, Error> = useQuery<IProfile, Error>(
    ['profile', username],
    async () => await getPerformerByUsername(username),
    {
      enabled: !!username && !hasEnoughPrefetchedData,
      staleTime: 15 * 1000,
      onSettled: resp => {
        // only validate query result when we actually queried
        if (hasEnoughPrefetchedData) return

        if (!resp) {
          addToast('error', t('modelDoesNotExist'))
          navigate('/404', { replace: true })
          return
        }

        if (resp?.role !== 'model') {
          addToast('error', t('modelDoesNotExist'))
          navigate('/404', { replace: true })
        }
      },
    }
  )

  useEffect(() => {
    if (!portal.classList.contains('portal--open')) {
      portal.classList.add('portal--open')
    }
  }, [portal])

  useEffect(() => {
    if (hasEnoughPrefetchedData) return

    if (error && (error as AxiosError)?.response?.status === 404) {
      addToast('error', t('modelDoesNotExist'))
      navigate('/404', { replace: true })
    }
  }, [error, hasEnoughPrefetchedData, navigate, t])

  if (!username) {
    return <Loader />
  }

  const creator = hasEnoughPrefetchedData ? (stateData as IProfile) : data

  if (!creator) {
    return <Loader />
  }

  return createPortal(
    <LiveStreamViewer
      username={username}
      creator={creator}
    />,
    portal
  )
}